import { ApiError } from '@libraries/typescript/api_client';

export type AllCaregiversInactiveError = {
    readonly kind: 'all_caregivers_inactive';
};

export type CaregiversSkipListedError = {
    readonly kind: 'caregivers_skip_listed';
    readonly message: string;
    readonly caregiverIds: ReadonlyArray<string>;
    readonly caregiverNames: ReadonlyArray<string>;
};

export type KnownApiError =
    | AllCaregiversInactiveError
    | CaregiversSkipListedError;

function isStringArray(value: unknown): value is Array<string> {
    return (
        Array.isArray(value) && value.every((item) => typeof item === 'string')
    );
}

/**
 * Returns whether central web policy owns this platform-transparent failure.
 *
 * Only the typed source discriminator establishes ownership. Header-less 429s
 * are product guardrails whose exact server detail must remain visible, so
 * status codes and message text are deliberately ignored.
 */
export function isTransparentRateLimitError(error: unknown): error is ApiError {
    return (
        error instanceof ApiError &&
        error.transparentRateLimitSource !== undefined
    );
}

export function parseKnownApiError(error: unknown): KnownApiError | null {
    if (!(error instanceof ApiError)) {
        return null;
    }

    if (error.status === 400) {
        const body = error.body as { detail?: unknown } | undefined;
        const detail = body?.detail;
        if (
            typeof detail === 'string' &&
            detail.startsWith('All selected caregivers are inactive')
        ) {
            return { kind: 'all_caregivers_inactive' };
        }
        if (typeof detail === 'object' && detail !== null) {
            const detailRecord = detail as Readonly<Record<string, unknown>>;
            const message = detailRecord['message'];
            const caregiverIds = detailRecord['excluded_caregiver_ids'];
            const caregiverNames = detailRecord['excluded_caregiver_names'];
            if (
                typeof message === 'string' &&
                isStringArray(caregiverIds) &&
                isStringArray(caregiverNames)
            ) {
                return {
                    kind: 'caregivers_skip_listed',
                    message,
                    caregiverIds,
                    caregiverNames,
                };
            }
        }
    }

    return null;
}

/**
 * The backend's plain-string `detail` from a FastAPI HTTPException (409/422/503,
 * etc.), or null when the error isn't a string-detail ApiError. Use to surface
 * the actionable server message instead of a generic toast.
 */
export function getApiErrorDetail(error: unknown): string | null {
    if (!(error instanceof ApiError)) {
        return null;
    }
    const body = error.body as { readonly detail?: unknown } | undefined;
    return typeof body?.detail === 'string' ? body.detail : null;
}

/**
 * A user-facing message for any thrown value. Use in a catch block that
 * surfaces the failure in a toast, so an unexpected non-Error rejection
 * still reads as text rather than `[object Object]`.
 */
export function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}
